/**
 * Orchestrates background initialization (lock removal, XML part setup, paragraph tagging)
 * that previously blocked React mount. Exposes a promise gate and a subscription API so
 * the React tree can display progress without any direct dependency on the init lifecycle.
 */

import {
  initCharStyleHistoryXmlPart,
  initStyleHistoryXmlPart,
  removeOrphanedLocks,
  tagAllParagraphs,
} from "src/services";

import type { TaggingResult, TaggingProgressEvent } from "src/services";

export type TaggingPhase =
  | "idle"
  | "removing-locks"
  | "init-xml"
  | "tagging"
  | "complete"
  | "error";

export interface TaggingStatus {
  phase: TaggingPhase;
  progress?: { taggedSoFar: number; totalToTag: number; totalParagraphs: number };
  error?: Error;
}

type Listener = (status: TaggingStatus) => void;

let status: TaggingStatus = { phase: "idle" };
let taggingPromise: Promise<TaggingResult> | null = null;
const listeners = new Set<Listener>();

function notify(next: TaggingStatus): void {
  status = next;
  listeners.forEach((cb) => {
    try {
      cb(status);
    } catch {
      // ignore listener errors
    }
  });
}

export function getStatus(): TaggingStatus {
  return status;
}

export function subscribe(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getTaggingPromise(): Promise<TaggingResult> {
  if (!taggingPromise) {
    // Lazily start if somehow called before startBackgroundInit
    startBackgroundInit();
  }
  return taggingPromise!;
}

export async function startBackgroundInit(): Promise<void> {
  if (taggingPromise) return;

  taggingPromise = _runInit();

  taggingPromise.catch(() => {
    // Already handled inside _runInit with status update; prevent unhandled rejection
  });
}

async function _runInit(): Promise<TaggingResult> {
  try {
    notify({ phase: "removing-locks" });

    // removeOrphanedLocks must complete before tagAllParagraphs because both touch content
    // controls. XML part inits are independent and can run in parallel.
    await Promise.all([
      removeOrphanedLocks(),
      initStyleHistoryXmlPart(),
      initCharStyleHistoryXmlPart(),
    ]);

    notify({ phase: "tagging" });

    const result = await tagAllParagraphs({
      batchSize: 500,
      onProgress: (event: TaggingProgressEvent) => {
        if (event.phase === "tagging") {
          notify({
            phase: "tagging",
            progress: {
              taggedSoFar: event.taggedSoFar,
              totalToTag: event.totalToTag,
              totalParagraphs: event.totalParagraphs,
            },
          });
        }
      },
    });

    notify({ phase: "complete" });
    return result;
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    console.error(`[tagging-init] init failed:`, error);
    notify({ phase: "error", error });
    throw error;
  }
}
